/**
 * Notebook persistence layer.
 *
 * Strategy:
 * - Primary: Supabase via the /api/notebook/* routes (server-side auth)
 * - Fallback: localStorage (anonymous / offline mode)
 *
 * The notebookId comes from the URL ([id] param). When the notebook has never
 * been saved to Supabase (e.g. first load), we create it on first save.
 *
 * Auto-save flow:
 *   1. store.js calls scheduleSectionSave(notebookId, sectionId, data)
 *   2. Each section has its own 400ms debounce timer
 *   3. PATCH /api/notebook/[id]/sections/[sectionId] is called with { data }
 *   4. On error, the payload is buffered in a write-queue and retried on reconnect
 */

import { buildTheme, lsKey as notebookLsKey } from '../models/notebook-model'
import { readNotebookLocalCache } from './notebook-local-cache'

const LS_KEY_PREFIX = 'folio_notebook_'

// Write queue for offline resilience
// Entries: { notebookId, sectionId, data, retries }
const writeQueue = []
const blockTimers = new Map()

let draining = false

/** Drop queued section writes for a notebook (session reset / stale ids). */
export function purgeWriteQueueForNotebook(notebookId) {
  if (!notebookId) return 0
  const before = writeQueue.length
  for (let i = writeQueue.length - 1; i >= 0; i -= 1) {
    if (writeQueue[i].notebookId === notebookId) {
      writeQueue.splice(i, 1)
    }
  }
  return before - writeQueue.length
}

async function drainQueue() {
  if (draining || writeQueue.length === 0) return
  draining = true
  while (writeQueue.length > 0) {
    const item = writeQueue[0]
    try {
      await patchSection(item.notebookId, item.sectionId, item.data)
      writeQueue.shift()
    } catch (err) {
      if (err instanceof NotebookApiError && err.status === 404) {
        writeQueue.shift()
        continue
      }
      item.retries++
      if (item.retries >= 5) writeQueue.shift() // give up after 5 retries
      break
    }
  }
  draining = false
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', drainQueue)
}

// ── Low-level fetch helpers ───────────────────────────────────────────────────

export class NotebookApiError extends Error {
  /**
   * @param {string} message
   * @param {{ status?: number, code?: string }} [meta]
   */
  constructor(message, meta = {}) {
    super(message)
    this.name = 'NotebookApiError'
    this.status = meta.status
    this.code = meta.code
  }
}

async function apiFetch(url, options = {}) {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    ...options,
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok || json.success === false) {
    throw new NotebookApiError(json?.error ?? `API error ${res.status}`, {
      status: res.status,
      code: json?.code,
    })
  }
  return json.data
}

// ── Notebook CRUD ─────────────────────────────────────────────────────────────

/** In-flight + resolved prefetch cache (hover from /home cards). TTL 30s. */
const notebookPrefetchById = new Map()

/**
 * Warm notebook API before navigation — re-used by fetchNotebook when still fresh.
 * @param {string} notebookId
 */
export function prefetchNotebook(notebookId) {
  if (!notebookId || typeof window === 'undefined') return
  const existing = notebookPrefetchById.get(notebookId)
  if (existing?.promise || existing?.data) return

  const promise = apiFetch(`/api/notebook/${notebookId}`)
    .then((data) => {
      notebookPrefetchById.set(notebookId, { data, at: Date.now() })
      return data
    })
    .catch((err) => {
      notebookPrefetchById.delete(notebookId)
      // Best-effort hover prefetch — must never throw to the UI (404 = no notebook row yet).
      if (process.env.NODE_ENV === 'development') {
        console.debug('[prefetchNotebook] skipped', notebookId, err?.message ?? err)
      }
      return null
    })

  notebookPrefetchById.set(notebookId, { promise, at: Date.now() })
}

function takePrefetchedNotebook(notebookId) {
  const entry = notebookPrefetchById.get(notebookId)
  if (!entry) return null
  const age = Date.now() - (entry.at ?? 0)
  if (age > 30_000) {
    notebookPrefetchById.delete(notebookId)
    return null
  }
  if (entry.data) {
    notebookPrefetchById.delete(notebookId)
    return entry.data
  }
  if (entry.promise) {
    return entry.promise.then((data) => {
      notebookPrefetchById.delete(notebookId)
      return data
    })
  }
  return null
}

export async function fetchNotebook(notebookId) {
  const prefetched = takePrefetchedNotebook(notebookId)
  if (prefetched) {
    try {
      const data = await prefetched
      if (data) return data
    } catch {
      /* fall through to live fetch */
    }
  }

  try {
    return await apiFetch(`/api/notebook/${notebookId}`)
    // returns { notebook, sections[] }
  } catch (err) {
    if (err instanceof NotebookApiError) throw err
    // Fallback to localStorage ONLY for network/offline failures.
    // For API/application errors, surface the error so we don't hydrate stale local data.
    const isNetworkError =
      err instanceof TypeError ||
      (err?.message ?? "").toLowerCase().includes("failed to fetch")
    if (!isNetworkError) throw err
    return readNotebookLocalCache(notebookId)
  }
}

export async function createNotebook(payload = {}) {
  return apiFetch('/api/notebook', {
    method: 'POST',
    body: JSON.stringify({
      title: payload.title ?? 'Untitled',
      icon: payload.icon ?? null,
      folder_id: payload.folder_id ?? null,
    }),
  })
}

export async function updateNotebookMeta(notebookId, patch) {
  return apiFetch(`/api/notebook/${notebookId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export async function deleteNotebook(notebookId) {
  return apiFetch(`/api/notebook/${notebookId}`, {
    method: 'DELETE',
  })
}

// ── Section persistence ───────────────────────────────────────────────────────

export async function addSection(notebookId, { type, data, sort_key, position }) {
  const res = await apiFetch(`/api/notebook/${notebookId}/sections`, {
    method: 'POST',
    body: JSON.stringify({ type, data, sort_key, position, page_id: null }),
  })
  return res // returns the created section row
}

// ── Tab / nested block persistence (notebook_blocks) ────────────────────────

export async function addNotebookBlock(notebookId, payload) {
  return apiFetch(`/api/notebook/${notebookId}/blocks`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function saveBlockData(notebookId, blockId, content) {
  return apiFetch(`/api/notebook/${notebookId}/blocks/${blockId}`, {
    method: 'PATCH',
    body: JSON.stringify({ content }),
  })
}

export async function deleteNotebookBlock(notebookId, blockId) {
  return apiFetch(`/api/notebook/${notebookId}/blocks/${blockId}`, {
    method: 'DELETE',
  })
}

export function scheduleBlockSave(notebookId, blockId, content, hooks) {
  if (!notebookId || !blockId) return
  clearTimeout(blockTimers.get(blockId))
  blockTimers.set(
    blockId,
    setTimeout(async () => {
      try {
        hooks?.onSaving?.()
        await saveBlockData(notebookId, blockId, content)
        hooks?.onSaved?.()
      } catch {
        hooks?.onError?.()
      } finally {
        blockTimers.delete(blockId)
      }
    }, 400),
  )
}

async function patchSection(notebookId, sectionId, data) {
  return apiFetch(`/api/notebook/${notebookId}/sections/${sectionId}`, {
    method: 'PATCH',
    body: JSON.stringify({ data }),
  })
}

/**
 * Main auto-save entry point.
 * Called by store.js after each section change (debounced 400ms per section).
 * On network failure, queues the write for retry.
 */
export async function saveSectionData(notebookId, sectionId, data) {
  try {
    await patchSection(notebookId, sectionId, data)
    drainQueue() // flush any queued writes if we're back online
    return { queued: false }
  } catch (err) {
    if (err instanceof NotebookApiError && err.status === 404) {
      return { queued: false, skipped: true }
    }
    writeQueue.push({ notebookId, sectionId, data, retries: 0 })
    throw err
  }
}

export async function deleteSection(notebookId, sectionId) {
  return apiFetch(`/api/notebook/${notebookId}/sections/${sectionId}`, {
    method: 'DELETE',
  })
}

export async function reorderSections(notebookId, items) {
  // items: [{ id, sort_key, page_id? }]
  return apiFetch(`/api/notebook/${notebookId}/sections`, {
    method: 'PUT',
    body: JSON.stringify(items),
  })
}

// ── Versions ──────────────────────────────────────────────────────────────────

export async function createSnapshot(notebookId, label = 'Auto-save') {
  return apiFetch(`/api/notebook/${notebookId}/versions`, {
    method: 'POST',
    body: JSON.stringify({ label }),
  })
}

export async function fetchVersions(notebookId) {
  return apiFetch(`/api/notebook/${notebookId}/versions`)
}

export async function restoreVersion(notebookId, versionId) {
  return apiFetch(`/api/notebook/${notebookId}/versions`, {
    method: 'PUT',
    body: JSON.stringify({ versionId }),
  })
}

// ── LocalStorage fallback (offline / anonymous) ───────────────────────────────

function lsKey(notebookId) {
  return notebookLsKey(notebookId)
}

// ── Spec API: load / save / auto-save ────────────────────────────────────────

/** @returns {Promise<{ notebook: object, sections: object[] } | null>} */
export async function loadNotebook(notebookId) {
  return fetchNotebook(notebookId)
}

/**
 * Persist notebook metadata + tab theme. Section payloads save via section PATCH.
 * @param {string} notebookId
 * @param {{ notebook: object, activeTabId: string, locked?: boolean }} payload
 */
export async function saveNotebook(notebookId, payload) {
  const { notebook, activeTabId, locked } = payload
  saveToLocalStorage(notebookId, payload)

  if (!notebookId) return

  await updateNotebookMeta(notebookId, {
    id: notebookId,
    title: notebook.title?.trim() || 'Untitled',
    icon: notebook.emoji ?? null,
    description: notebook.subtitle ?? null,
    theme: buildTheme(notebook, activeTabId),
    ...(typeof locked === 'boolean' ? { is_locked: locked } : {}),
  })
}

/**
 * Debounced full-notebook metadata save when store is dirty.
 * @param {import('zustand').StoreApi} store
 * @param {number} intervalMs
 */
export function watchAndAutoSave(store, intervalMs = 3000) {
  let timer = null
  let prev = store.getState()

  const flush = async () => {
    const state = store.getState()
    if (!state.isDirty || !state.notebookId || state.isSaving) return
    store.setState({ isSaving: true })
    try {
      await saveNotebook(state.notebookId, {
        notebook: state.notebook,
        activeTabId: state.activeTabId,
        locked: state.locked,
      })
      store.setState({
        isDirty: false,
        isSaving: false,
        lastSaved: new Date(),
      })
    } catch {
      store.setState({ isSaving: false })
    }
  }

  return store.subscribe((state) => {
    if (state.isDirty && !state.isSaving) {
      clearTimeout(timer)
      timer = setTimeout(flush, intervalMs)
    }
    prev = state
  })
}

export function loadFromLocalStorage(notebookId) {
  return readNotebookLocalCache(notebookId)
}

export function saveToLocalStorage(notebookId, payload) {
  try {
    localStorage.setItem(lsKey(notebookId), JSON.stringify({
      ...payload,
      savedAt: new Date().toISOString(),
    }))
  } catch { /* quota exceeded, ignore */ }
}

/** @deprecated — v1 key used by the old single-notebook localStorage system */
export function isCloudEnabled() {
  return typeof window !== 'undefined' && !!process.env.NEXT_PUBLIC_SUPABASE_URL
}
